import { isThirdPartyLogistics } from "@redotech/account-common/team-type";
import { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import {
  GlobalSearchHistoryItemType,
  GlobalSearchItem,
} from "@redotech/merchant-sdk/rpc/schema/global-search/global-search-schema";
import { useRequiredContext } from "@redotech/react-util/context";
import { t } from "@redotech/redo-i18n";
import {
  MerchantRoutes,
  useMerchantRoutes,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { useReturnsNewSettings } from "@redotech/redo-merchant-app-common/settings/returns-new-settings-context";
import { useShippingNewSettings } from "@redotech/redo-merchant-app-common/shipping-new-settings-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { getOrderNavigationUrl } from "@redotech/redo-merchant-fulfillment-app/common/order-navigation-url";
import { GlobalSearchCategory } from "@redotech/redo-views-common/global-search/global-search-category";
import {
  CommandPaletteItem,
  SearchParams,
} from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import { filterTruthy } from "@redotech/util/array";
import { useCallback } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { shipmentStatusName } from "../../../return/util";
import { getWithdrawalStatusLabel } from "../../../return/withdrawals/withdrawals-list-labels";
import { usePageSearch } from "./global-search-tools/use-page-search";
import { useSettingsItems } from "./settings";
import {
  removeSettingsNavigationDuplicatePageResults,
  searchSettingsNavigationItems,
} from "./settings-navigation-search";

export function useSearchItems(merchantRpcClient: RedoMerchantRpcClient) {
  const routes = useMerchantRoutes();
  const team = useRequiredContext(TeamContext);
  const onlyAssignedFulfillments = isThirdPartyLogistics(team);
  const navigate = useNavigate();
  const shippingNewSettings = useShippingNewSettings();
  const returnsNewSettings = useReturnsNewSettings();
  const searchPages = usePageSearch({
    includeSettingsMetadata: shippingNewSettings,
  });
  const settingsItems = useSettingsItems();

  return useCallback(
    async ({
      query,
      filters,
      signal,
    }: SearchParams): Promise<CommandPaletteItem[]> => {
      if (!query.trim()) {
        return [];
      }

      // Determine category filter
      const category = onlyAssignedFulfillments
        ? "shipments"
        : filters.length === 1
          ? (filters[0] as GlobalSearchCategory)
          : undefined;

      // Search pages client-side
      const shouldSearchPages = !category || category === "pages";
      const settingsNavigationResults = shouldSearchPages
        ? searchSettingsNavigationItems({
            query,
            routes,
            sections: settingsItems ?? [],
            navigate,
            limit: category === "pages" ? 10 : 3,
            viewerVisibility: "merchant",
            shippingNewSettings,
            returnsNewSettings,
          })
        : [];
      const pageResults = shouldSearchPages
        ? searchPages(query, category === "pages" ? 10 : 3)
        : [];
      const dedupedPageResults = shouldSearchPages
        ? removeSettingsNavigationDuplicatePageResults({
            pageResults,
            settingsResults: settingsNavigationResults,
          })
        : [];

      // If only searching pages, return early
      if (category === "pages") {
        return [...settingsNavigationResults, ...dedupedPageResults].slice(
          0,
          10,
        );
      }

      // Fetch global search results from backend
      let globalSearchResults: CommandPaletteItem[] = [];
      try {
        const response = await merchantRpcClient.getGlobalSearchResults(
          { searchText: query, category },
          { signal },
        );

        globalSearchResults = response.results.map((item: GlobalSearchItem) =>
          globalSearchItemToCommandPaletteItem(item, routes, navigate),
        );
      } catch (error: unknown) {
        // Re-throw abort errors so caller can handle them appropriately
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }
        console.warn("Failed to fetch global search results:", error);
      }

      // Combine results: pages + backend results
      return [
        ...settingsNavigationResults,
        ...dedupedPageResults,
        ...globalSearchResults,
      ];
    },
    [
      merchantRpcClient,
      onlyAssignedFulfillments,
      routes,
      navigate,
      searchPages,
      settingsItems,
      shippingNewSettings,
      returnsNewSettings,
    ],
  );
}

function globalSearchItemToCommandPaletteItem(
  result: GlobalSearchItem,
  routes: MerchantRoutes,
  navigate: NavigateFunction,
): CommandPaletteItem {
  switch (result.type) {
    case GlobalSearchHistoryItemType.ORDER:
      return {
        id: `order-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.orderCreatedAt.toLocaleDateString(),
          `$${result.price.toFixed(2)}`,
          t("{{count}} items", {
            count: result.items,
            context: "number of items in an order search result",
          }),
          shipmentStatusName(result.fulfillmentStatus),
        ]).join(" | "),
        category: "orders",
        group: t("Orders", {
          context: "navigation category for merchant sales orders",
        }),
        action: ({ newTab }) => {
          const href = `${routes.shippingBaseUrl}/outbound-labels/orders/${result.id}`;
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.SHIPMENT:
      return {
        id: `shipment-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.recipientName,
          result.shipmentCreatedAt.toLocaleDateString(),
          result.trackingCode,
          shipmentStatusName(result.shipmentStatus),
        ]).join(" | "),
        category: "shipments",
        group: t("Fulfillments", {
          context:
            "search category for merchant orders being picked, packed, and shipped from a warehouse, not abstract completion",
        }),
        action: ({ newTab }) => {
          const href = getOrderNavigationUrl({
            fulfillmentGroupId: result.id,
            orderId: { id: result.externalOrderId },
            ordersOutboundLabelsHref: routes.ordersOutboundLabelsHref,
          });
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.CUSTOMER:
      return {
        id: `customer-${result.id}`,
        title: result.customerName,
        subtitle: filterTruthy([result.email, result.phone]).join(" | "),
        category: "customers",
        group: t("Customers", {
          context: "navigation category for merchant customer records",
        }),
        action: ({ newTab }) => {
          const href = routes.getCustomerRecordHref({ customerId: result.id });
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.RETURN:
      return {
        id: `return-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.returnStartedAtDate.toLocaleDateString(),
          result.returnValue != null
            ? `$${result.returnValue.toFixed(2)}`
            : null,
          result.returnType,
          shipmentStatusName(result.returnStatus),
        ]).join(" | "),
        category: "returns",
        group: t("Returns", {
          context: "navigation category for customer product returns",
        }),
        action: ({ newTab }) => {
          const href = `${routes.returnsHref}/all-returns/${result.id}`;
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.WITHDRAWAL:
      return {
        id: `withdrawal-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.createdAt.toLocaleDateString(),
          getWithdrawalStatusLabel(result.status),
        ]).join(" | "),
        category: "withdrawals",
        group: t("Withdrawals", {
          context:
            "search category for customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
        }),
        action: ({ newTab }) => {
          const href = `${routes.getWithdrawalSubRoute("all-withdrawals")}/${result.id}`;
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.CONVERSATION:
      return {
        id: `conversation-${result.id}`,
        title: result.customer,
        subtitle: filterTruthy([
          result.createdAt.toLocaleDateString(),
          result.subject,
          result.platform,
        ]).join(" | "),
        category: "conversations",
        group: t("Conversations", {
          context: "navigation category for customer support conversations",
        }),
        action: ({ newTab }) => {
          const href = routes.getConversationHref(result.id);
          if (newTab) {
            window.open(href, "_blank");
          } else {
            navigate(href);
          }
        },
      };

    case GlobalSearchHistoryItemType.DOCUMENTATION:
      return {
        id: `article-${result.id}`,
        title: result.title,
        subtitle: result.description || undefined,
        category: "help-articles",
        group: t("Help Articles", {
          context: "navigation category for product help documentation",
        }),
        action: () => {
          window.open(result.url, "_blank");
        },
      };

    case GlobalSearchHistoryItemType.PAGE:
      return {
        id: `page-${result.id}`,
        title:
          result.breadcrumbs[result.breadcrumbs.length - 1] ||
          t("Page", { context: "generic merchant app page title" }),
        subtitle: result.breadcrumbs.slice(0, -1).join(" > ") || undefined,
        category: "pages",
        group: t("Pages", {
          context: "navigation category for merchant app pages",
        }),
        action: ({ newTab }) => {
          // Page id is the route path
          if (newTab) {
            window.open(result.id, "_blank");
          } else {
            navigate(result.id);
          }
        },
      };

    default: {
      // Type guard to ensure exhaustive check
      const _exhaustiveCheck: never = result;
      throw new Error(`Unhandled GlobalSearchItem type: ${_exhaustiveCheck}`);
    }
  }
}
